import classNames from 'classnames';
import { MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDocumentProgress } from '../../hooks/useDocument';
import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';

import BasicButton from '../buttons/BasicButton';
import PlayIcon from '../icons/PlayIcon';
import Picture from '../partials/Picture';

import styles from '../../styles/lists/podcast-playlist.module.css';

interface PodcastPlaylistItemProps {
    item: DocumentSnippet;
    index: number;
    current?: boolean;
    playing?: boolean;
    onPlay?: ((item: DocumentSnippet) => void) | null;
}

function PodcastPlaylistItem({
    item,
    index,
    current = false,
    playing = false,
    onPlay = null,
}: PodcastPlaylistItemProps) {
    const { id = null, title = null, image = null, episode = null, duration = null } = item || {};
    const intl = useIntl();
    const url = useDocumentUrlGenerator();
    const { progress = null } = useDocumentProgress(id);
    const listened = !current && progress !== null && progress >= 0.98;
    const started = progress !== null && progress > 0 && !listened;
    const number = episode || `${index + 1}`.padStart(2, '0');

    const onClick = (event: MouseEvent) => {
        // A click with a modifier keeps the link (new tab or window)
        if (onPlay === null || event.metaKey || event.ctrlKey || event.shiftKey) {
            return;
        }
        // The player shows the episode itself, on the same page
        event.preventDefault();
        onPlay(item);
    };

    return (
        <li className={styles.item}>
            <BasicButton
                href={url(item)}
                className={classNames([
                    styles.button,
                    { [styles.current]: current, [styles.listened]: listened },
                ])}
                onClick={onClick}
                aria-current={current ? 'true' : undefined}
                clickRef="podcast_playlist"
            >
                <span className={styles.thumbnail}>
                    {image !== null ? (
                        <Picture
                            media={image}
                            size="small"
                            loading="lazy"
                            className={styles.image}
                        />
                    ) : (
                        <span className={styles.number}>{number}</span>
                    )}
                    {current ? (
                        <span className={styles.overlay}>
                            {playing ? (
                                <span className={styles.equalizer} aria-hidden="true">
                                    <span />
                                    <span />
                                    <span />
                                </span>
                            ) : (
                                <PlayIcon className={styles.playIcon} />
                            )}
                        </span>
                    ) : null}
                </span>
                <span className={styles.label}>
                    <span className={styles.episode}>
                        <FormattedMessage
                            defaultMessage="Ép. {episode}"
                            description="Podcast playlist item"
                            values={{ episode: number }}
                        />
                        {duration !== null && duration > 0 ? (
                            <>
                                {' · '}
                                <FormattedMessage
                                    defaultMessage="{minutes} min"
                                    description="Podcast playlist item"
                                    values={{ minutes: Math.max(Math.round(duration / 60), 1) }}
                                />
                            </>
                        ) : null}
                    </span>
                    <span className={styles.title}>{title}</span>
                    {started ? (
                        <span className={styles.progress}>
                            <span
                                className={styles.progressBar}
                                style={{ width: `${Math.round(progress * 100)}%` }}
                            />
                        </span>
                    ) : null}
                </span>
                {listened ? (
                    <svg
                        className={styles.check}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        role="img"
                        aria-label={intl.formatMessage({
                            defaultMessage: 'Écouté',
                            description: 'Podcast playlist item listened',
                        })}
                    >
                        <path d="m5 12 5 5 9-10" />
                    </svg>
                ) : null}
            </BasicButton>
        </li>
    );
}

export interface PodcastPlaylistProps {
    title?: string | null;
    seasons?: CollectionSnippet[] | null;
    seasonId?: string | null;
    items?: DocumentSnippet[] | null;
    currentId?: string | null;
    playing?: boolean;
    autoplayNext?: boolean;
    className?: string | null;
    onChangeSeason?: ((seasonId: string) => void) | null;
    onToggleAutoplay?: (() => void) | null;
    onPlayItem?: ((item: DocumentSnippet) => void) | null;
}

function PodcastPlaylist({
    title = null,
    seasons = null,
    seasonId = null,
    items = null,
    currentId = null,
    playing = false,
    autoplayNext = true,
    className = null,
    onChangeSeason = null,
    onToggleAutoplay = null,
    onPlayItem = null,
}: PodcastPlaylistProps) {
    const intl = useIntl();
    const hasSeasons = seasons !== null && seasons.length > 1;

    return (
        <section
            className={classNames([styles.container, className])}
            aria-label={intl.formatMessage({
                defaultMessage: 'Épisodes',
                description: 'Podcast playlist label',
            })}
        >
            <header className={styles.header}>
                <div className={styles.surtitle}>
                    {items !== null ? (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {# épisode} other {# épisodes}}"
                            description="Podcast playlist count"
                            values={{ count: items.length }}
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Épisodes"
                            description="Podcast playlist label"
                        />
                    )}
                </div>
                {title !== null ? <h2 className={styles.serieTitle}>{title}</h2> : null}
                <div className={styles.controls}>
                    {hasSeasons ? (
                        <div className={styles.seasons}>
                            {seasons.map(({ id, title: seasonTitle }) => (
                                <BasicButton
                                    key={`season-${id}`}
                                    className={classNames([
                                        styles.season,
                                        { [styles.active]: id === seasonId },
                                    ])}
                                    aria-pressed={id === seasonId ? 'true' : 'false'}
                                    onClick={
                                        onChangeSeason !== null ? () => onChangeSeason(id) : null
                                    }
                                >
                                    {seasonTitle}
                                </BasicButton>
                            ))}
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className={styles.autoplay}
                        role="switch"
                        aria-checked={autoplayNext}
                        onClick={onToggleAutoplay || undefined}
                    >
                        <span className={styles.autoplayLabel}>
                            <FormattedMessage
                                defaultMessage="Lecture auto"
                                description="Podcast playlist autoplay toggle"
                            />
                        </span>
                        <span
                            className={classNames([
                                styles.switch,
                                { [styles.switchOn]: autoplayNext },
                            ])}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </header>
            <ol className={styles.items}>
                {(items || []).map((item, index) => (
                    <PodcastPlaylistItem
                        key={`item-${item.id}`}
                        item={item}
                        index={index}
                        current={item.id === currentId}
                        playing={item.id === currentId && playing}
                        onPlay={onPlayItem}
                    />
                ))}
            </ol>
        </section>
    );
}

export default PodcastPlaylist;
